<template>
  <div class="floorplan-section">
    <h1 class="floorplan-title">Floor Plan & Price List</h1>
    <p class="floorplan-description">
      Explore our range of floor plans designed to suit your lifestyle.
    </p>

    <!-- Floor Plan Cards -->
    <v-container fluid class="pa-0">
      <v-row align="stretch">
        <v-col v-for="(plan, index) in floorPlans" :key="index" cols="12" sm="6" md="4">
          <v-card class="floorplan-card pa-4 d-flex flex-column">
            <v-img :src="plan.image" alt="Floor Plan" height="200px" contain class="floorplan-image"></v-img>
            <v-card-title class="floorplan-card-title">{{ plan.title }}</v-card-title>
            <v-card-text class="floorplan-card-text flex-grow-1">{{ plan.description }}</v-card-text>
            <v-btn class="floorplan-card-button" @click="scrollToSection('enquire')">Find Out More</v-btn>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Download Buttons -->
    <div class="download-buttons">
      <v-btn color="#1E3836" class="download-button" @click="openModal">Download Floor Plan</v-btn>
      <v-btn color="#1E3836" class="download-button" @click="openModal">Download Price List</v-btn>
      <ModalForm :isOpen="isModalOpen" @close="closeModal" />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import ModalForm from '@/views/ModalForm.vue';

export default defineComponent({
  name: 'AboutView',
  components: {
    ModalForm,
  },
  props: {
    scrollToSection: Function, // Define the prop to accept the method
  },
  data() {
    return {
      floorPlans: [
        { title: '1-Bedroom A1', description: 'A sleek and efficient layout, perfect for urban professionals and modern living.', image: require('@/assets/floorplan/fp1.png') },
        { title: '2-Bedroom B1', description: 'A well-optimized space offering a seamless blend of comfort and functionality.', image: require('@/assets/floorplan/fp2.png') },
        { title: '2-Bedroom B2', description: 'Designed for maximum space and natural light, creating a bright and airy home.', image: require('@/assets/floorplan/fp3.png') },
        { title: '2-Bedroom B3', description: 'An ideal choice for small families, offering a balance of privacy and openness.', image: require('@/assets/floorplan/fp4.png') },
        { title: '3-Bedroom C1', description: 'A spacious modern home with premium finishes and well-crafted interiors.', image: require('@/assets/floorplan/fp5.png') },
        { title: '3-Bedroom C2', description: 'A refined and functional layout, ideal for families seeking extra comfort.', image: require('@/assets/floorplan/fp6.png') },
        { title: '3-Bedroom C3', description: 'Luxury meets practicality with generous living spaces and elegant details.', image: require('@/assets/floorplan/fp7.png') },
        { title: '3-Bedroom C4', description: 'A stylish and expansive residence designed for a sophisticated lifestyle.', image: require('@/assets/floorplan/fp8.png') },
        { title: '4-Bedroom D1', description: 'An elegant and spacious home, perfect for multi-generational living.', image: require('@/assets/floorplan/fp9.png') },
        { title: '4-Bedroom D2', description: 'A grand residence featuring expansive interiors and premium finishes.', image: require('@/assets/floorplan/fp10.png') },
        { title: '4-Bedroom D2b', description: 'A thoughtfully designed layout offering luxury, comfort, and privacy.', image: require('@/assets/floorplan/fp11.png') },
        { title: '4-Bedroom DS', description: 'An exclusive duplex residence with unparalleled space and sophistication.', image: require('@/assets/floorplan/fp12.png') },
      ],
      isModalOpen: false,

    };
  },
  methods: {
    openModal() {
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },
  },
});
</script>

<style scoped>
.floorplan-section {
  padding: 40px 20px;
  background-color: transparent; /* White background */
  text-align: center;
}

.floorplan-title {
  font-size: 2.5rem;
  font-weight: bold;
  color: #2c3e50; /* Dark text color */
  margin-bottom: 16px;
}

.floorplan-description {
  font-size: 1.1rem;
  color: #666; /* Subdued text color */
  margin-bottom: 32px;
}

.floorplan-card {
  background-color: #F7F4ED;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); /* Subtle shadow */
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  height: 100%; /* Ensure all cards are the same height */
}

.floorplan-card:hover {
  transform: translateY(-4px); /* Lift effect on hover */
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2); /* Enhanced shadow on hover */
}

.floorplan-image {
  border-radius: 8px 8px 0 0;
  filter: blur(2px); /* Apply slight blur */
  transition: filter 0.3s ease-in-out;
}

.floorplan-card:hover .floorplan-image {
  filter: blur(0); /* Remove blur on hover */
}

.floorplan-card-title {
  font-size: 1.25rem;
  font-weight: bold;
  color: #2c3e50; /* Primary color */
  margin-bottom: 12px;
}

.floorplan-card-text {
  font-size: 1rem;
  color: #555; /* Subdued text color */
  line-height: 1.5;
  margin-bottom: 16px;
}

.floorplan-card-button {
  font-size: 0.9rem;
  font-weight: bold;
  text-transform: uppercase; /* Uppercase text */
  padding: 8px 16px;
  border-radius: 8px; /* Rounded corners */
  transition: background-color 0.3s ease;
}

.floorplan-card-button:hover {
  background-color: #e0e0e0; /* Hover effect */
}

.download-buttons {
  display: flex;
  justify-content: center;
  gap: 16px; /* Space between buttons */
  margin-top: 32px;
}

.download-button {
  font-size: 1rem;
  font-weight: bold;
  transition: transform 0.2s ease, box-shadow 0.2s ease; /* Smooth hover effects */
}

.download-button:hover {
  transform: translateY(-2px); /* Slight lift on hover */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2); /* Shadow on hover */
}

/* Responsive Adjustments */
@media (max-width: 600px) {
  .floorplan-section {
    padding: 20px 10px; /* Reduce padding on mobile */
  }

  .floorplan-title {
    font-size: 2rem; /* Smaller title on mobile */
  }

  .floorplan-description {
    font-size: 1rem; /* Smaller description on mobile */
  }

  .floorplan-card-title {
    font-size: 1.1rem; /* Smaller card title on mobile */
  }

  .floorplan-card-text {
    font-size: 0.9rem; /* Smaller card text on mobile */
  }

  .floorplan-card-button {
    font-size: 0.8rem; /* Smaller button text on mobile */
    padding: 6px 12px; /* Adjust padding for mobile */
    width: 100%; /* Full-width button on mobile */
  }

  .download-buttons {
    flex-direction: column; /* Stack buttons vertically on mobile */
    gap: 12px; /* Reduce gap between buttons */
  }

  .download-button {
    width: 100%; /* Full-width buttons on mobile */
    font-size: 0.9rem; /* Smaller button text on mobile */
    padding: 10px 20px; /* Adjust padding for mobile */
  }
}
</style>